import React, { useState, useRef, useEffect } from 'react';
import { createChangelogEntry, updateChangelogEntry } from '../../api/changelog';
import { getSignedUrl } from '../../api/upload';
import { AUDIENCES, CATEGORIES, PLATFORMS } from '../../constants/changelog_constants';
import ShopmyImage from '../General/ShopmyImage';
import './Changelog.scss';

const EXTENSION_TO_TYPE = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp' };

const todayISODate = () => new Date().toISOString().split('T')[0];

// MySQL/Knex returns dates as full ISO strings; <input type="date"> wants YYYY-MM-DD
const toDateInputValue = raw => {
  if (!raw) return todayISODate();
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return s.split('T')[0] || todayISODate();
};

// Convert markdown to HTML for the WYSIWYG editor on load
const markdownToHtml = md => {
  if (!md) return '';
  // If it already contains tags, treat as HTML (e.g. previously-saved HTML)
  if (/<(p|ul|li|strong|em|br|div)\b/i.test(md)) return md;
  const escapeHtml = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = md.split('\n');
  const out = [];
  let inList = false;
  for (const line of lines) {
    let l = escapeHtml(line);
    l = l.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
         .replace(/\*(.+?)\*/g, '<em>$1</em>')
         .replace(/_(.+?)_/g, '<em>$1</em>');
    if (/^[-•]\s+/.test(line)) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${l.replace(/^[-•]\s+/, '')}</li>`);
    } else {
      if (inList) { out.push('</ul>'); inList = false; }
      if (line.trim()) out.push(`<div>${l}</div>`);
      else out.push('<div><br></div>');
    }
  }
  if (inList) out.push('</ul>');
  return out.join('');
};

// Convert the contentEditable's HTML back to markdown for saving
const htmlToMarkdown = html => {
  if (!html) return '';
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  const walk = node => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const tag = node.tagName.toLowerCase();
    const inner = Array.from(node.childNodes).map(walk).join('');
    if (tag === 'strong' || tag === 'b') return `**${inner}**`;
    if (tag === 'em' || tag === 'i') return `*${inner}*`;
    if (tag === 'u') return inner; // underline -> no markdown equivalent, drop
    if (tag === 'br') return '\n';
    if (tag === 'p' || tag === 'div') return inner + '\n';
    if (tag === 'ul' || tag === 'ol') {
      return Array.from(node.children)
        .map(li => `- ${Array.from(li.childNodes).map(walk).join('').trim()}`)
        .join('\n') + '\n';
    }
    if (tag === 'li') return `- ${inner}`;
    return inner;
  };
  return Array.from(wrapper.childNodes).map(walk).join('').replace(/\n{3,}/g, '\n\n').trim();
};

const emptyForm = {
  title: '',
  description: '',
  date: todayISODate(),
  version: '',
  category: [],
  audiences: [],
  platform: [],
  attachments: [],
  loom_url: '',
  status: 'published',
};

const ChangelogEntryModal = ({ entry, onSave, onClose }) => {
  const [form, setForm] = useState(entry ? {
    title: entry.title || '',
    description: entry.description || '',
    date: toDateInputValue(entry.date),
    version: entry.version || '',
    category: entry.category || [],
    audiences: entry.audiences || [],
    platform: entry.platform || [],
    attachments: entry.attachments || [],
    loom_url: entry.loom_url || '',
    status: entry.status || 'published',
  } : emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const descRef = useRef(null);
  const backdropMouseDownRef = useRef(false);

  // Mount the existing description (markdown) into the editor as HTML, once
  useEffect(() => {
    if (descRef.current) {
      descRef.current.innerHTML = markdownToHtml(form.description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = command => {
    document.execCommand(command, false, null);
    descRef.current?.focus();
    setField('description', descRef.current?.innerHTML || '');
  };

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const toggleArrayField = (key, value) => {
    setForm(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const isMobile = form.platform.includes('mobile');

  const uploadFile = async file => {
    const ext = file.name.split('.').pop().toLowerCase();
    const contentType = EXTENSION_TO_TYPE[ext];
    if (!contentType) {
      setError('Only JPEG, PNG, GIF, and WebP images are supported.');
      return;
    }
    setUploadingImage(true);
    setError(null);
    try {
      const signedUrl = await getSignedUrl({ objectName: file.name, contentType });
      await fetch(signedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
      const finalUrl = signedUrl.split('?')[0];
      setForm(prev => ({ ...prev, attachments: [...prev.attachments, finalUrl] }));
    } catch {
      setError('Image upload failed. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUpload = async e => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      await uploadFile(file);
    }
  };

  const handleDrop = async e => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith('image/'));
    for (const file of files) {
      await uploadFile(file);
    }
  };

  const handleDragOver = e => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    // Only clear dragging when leaving the drop zone itself, not children
    if (e.currentTarget === e.target) setDragging(false);
  };

  const removeAttachment = url => {
    setForm(prev => ({ ...prev, attachments: prev.attachments.filter(u => u !== url) }));
  };

  const handleSave = async (statusOverride) => {
    const status = statusOverride || form.status;
    if (!form.title.trim() || !form.date) {
      setError('Title and date are required.');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = {
      title: form.title.trim(),
      description: htmlToMarkdown(form.description),
      date: form.date,
      version: form.version.trim() || null,
      category: form.category,
      audiences: form.audiences,
      platform: form.platform,
      attachments: form.attachments,
      loom_url: form.loom_url.trim() || null,
      status,
    };

    try {
      const saved = entry?.id
        ? await updateChangelogEntry(entry.id, payload)
        : await createChangelogEntry(payload);
      onSave(saved);
    } catch (err) {
      const backendMsg = err?.response?.data?.error || err?.response?.data?.msg;
      setError(backendMsg || 'Failed to save entry. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div
      className='changelog-modal-backdrop'
      onMouseDown={e => { if (e.target === e.currentTarget) backdropMouseDownRef.current = true; }}
      onMouseUp={e => {
        if (e.target === e.currentTarget && backdropMouseDownRef.current) onClose();
        backdropMouseDownRef.current = false;
      }}
    >
      <div className='changelog-modal'>
        <div className='changelog-modal-header'>
          <h2>{entry ? 'Edit Entry' : 'New Changelog Entry'}</h2>
          <button className='changelog-modal-close' onClick={onClose}>×</button>
        </div>

        <div className='changelog-modal-body'>
          {error && <div className='changelog-modal-error'>{error}</div>}

          <div className='changelog-form-row'>
            <label>Title *</label>
            <input
              type='text'
              value={form.title}
              onChange={e => setField('title', e.target.value)}
              placeholder='Entry title'
            />
          </div>

          <div className='changelog-form-row'>
            <label>Description</label>
            <div className='cl-rte-wrap'>
              <div className='cl-rte-toolbar'>
                <button type='button' className='cl-rte-btn' onMouseDown={e => { e.preventDefault(); exec('bold'); }} title='Bold'>
                  <strong>B</strong>
                </button>
                <button type='button' className='cl-rte-btn' onMouseDown={e => { e.preventDefault(); exec('italic'); }} title='Italic'>
                  <em>I</em>
                </button>
                <button type='button' className='cl-rte-btn' onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }} title='Bullet list'>
                  • List
                </button>
              </div>
              <div
                ref={descRef}
                className='cl-rte-editor'
                contentEditable
                suppressContentEditableWarning
                onInput={() => setField('description', descRef.current?.innerHTML || '')}
                data-placeholder='Describe what changed and why it matters...'
              />
            </div>
          </div>

          <div className='changelog-form-row'>
            <label>Date *</label>
            <input
              type='date'
              value={form.date}
              onChange={e => setField('date', e.target.value)}
            />
          </div>

          <div className='changelog-form-row'>
            <label>Type</label>
            <div className='changelog-form-checkboxes'>
              {CATEGORIES.map(opt => (
                <label key={opt} className='changelog-checkbox-label'>
                  <input
                    type='checkbox'
                    checked={form.category.includes(opt)}
                    onChange={() => toggleArrayField('category', opt)}
                  />
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </label>
              ))}
            </div>
          </div>

          <div className='changelog-form-row'>
            <label>Audience</label>
            <div className='changelog-form-checkboxes'>
              {AUDIENCES.map(opt => (
                <label key={opt} className='changelog-checkbox-label'>
                  <input
                    type='checkbox'
                    checked={form.audiences.includes(opt)}
                    onChange={() => toggleArrayField('audiences', opt)}
                  />
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </label>
              ))}
            </div>
          </div>

          <div className='changelog-form-row'>
            <label>Platform</label>
            <div className='changelog-form-checkboxes'>
              {PLATFORMS.map(opt => (
                <label key={opt} className='changelog-checkbox-label'>
                  <input
                    type='checkbox'
                    checked={form.platform.includes(opt)}
                    onChange={() => toggleArrayField('platform', opt)}
                  />
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </label>
              ))}
            </div>
          </div>

          {isMobile && (
            <div className='changelog-form-row'>
              <label>Mobile Version</label>
              <div className='cl-version-prefix-wrap'>
                <span className='cl-version-prefix'>v</span>
                <input
                  type='text'
                  className='cl-version-input'
                  value={form.version}
                  onChange={e => setField('version', e.target.value)}
                  placeholder='1.0.0'
                />
              </div>
            </div>
          )}

          <div className='changelog-form-row'>
            <label>Images</label>
            <div
              className={`cl-image-dropzone${dragging ? ' cl-image-dropzone--active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {form.attachments.length > 0 && (
                <div className='cl-attachment-thumbs'>
                  {form.attachments.map(url => (
                    <div key={url} className='cl-attachment-thumb'>
                      <ShopmyImage source={url} alt='Attachment' className='cl-attachment-thumb-img' />
                      <button
                        type='button'
                        className='cl-attachment-thumb-remove'
                        onClick={() => removeAttachment(url)}
                        title='Remove'
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              <input
                ref={fileInputRef}
                type='file'
                multiple
                accept={Object.keys(EXTENSION_TO_TYPE).map(e => `.${e}`).join(',')}
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
              <div className='cl-image-dropzone-hint'>
                {dragging
                  ? 'Drop images to upload'
                  : (uploadingImage ? 'Uploading…' : 'Drag and drop images here, or')}
              </div>
              <button
                type='button'
                className='cl-upload-img-btn'
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
              >
                {uploadingImage ? 'Uploading…' : '+ Add Image'}
              </button>
            </div>
          </div>

          <div className='changelog-form-row'>
            <label>Loom URL</label>
            <input
              type='url'
              value={form.loom_url}
              onChange={e => setField('loom_url', e.target.value)}
              placeholder='https://www.loom.com/share/...'
            />
          </div>
        </div>

        <div className='changelog-modal-footer'>
          <button className='changelog-btn changelog-btn--secondary' onClick={() => handleSave('draft')} disabled={saving}>
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button className='changelog-btn changelog-btn--primary' onClick={() => handleSave('published')} disabled={saving}>
            {saving ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangelogEntryModal;
